import { NavLink } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { usePendingInvitations } from '@/hooks/useShares';
import { InvitationsNavIcon, UnassignedSpendingNavIcon } from '@/components/sharing/SharingUi';
import { navIcons } from '@/components/layout/navIcons';
import { UserAvatar } from '@/components/user/UserAvatar';
import { useUnassignedSpending } from '@/hooks/useUnassignedSpending';
import { cn } from '@/lib/utils';
import type { ReactNode } from 'react';

const navItems: { to: string; label: string; icon: ReactNode; end?: boolean }[] = [
  // Reuse the former Transactions icon for Dashboard; /transactions stays for deep links.
  { to: '/dashboard', label: 'Dashboard', icon: navIcons.transactions, end: true },
  { to: '/budgets', label: 'Budgets', icon: navIcons.budgets },
];

export function BottomNav() {
  const { user } = useAuth();
  const { pendingCount } = usePendingInvitations();
  const { data: unassigned } = useUnassignedSpending();
  const unassignedCount = unassigned?.count ?? 0;

  const settingsAvatar = (
    <UserAvatar
      picture={user?.picture}
      name={user?.name ?? user?.givenName ?? user?.displayName}
      className="h-5 w-5"
    />
  );

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-40 border-t bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 pb-[env(safe-area-inset-bottom,0px)] md:hidden">
      <div className="flex h-16 items-center justify-around px-1">
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={({ isActive }) =>
              cn(
                'flex flex-col items-center justify-center gap-1 px-2 py-2 text-xs transition-colors',
                isActive
                  ? 'text-primary'
                  : 'text-muted-foreground hover:text-foreground'
              )
            }
          >
            <span className="h-5 w-5">{item.icon}</span>
            <span>{item.label}</span>
          </NavLink>
        ))}
        <div className="flex flex-col items-center justify-center gap-1 px-1.5 py-2 text-xs">
          <InvitationsNavIcon pendingCount={pendingCount} iconClassName="h-5 w-5" />
          <span className={pendingCount > 0 ? 'text-primary' : 'text-muted-foreground'}>Invites</span>
        </div>
        {unassignedCount > 0 && (
          <div className="flex flex-col items-center justify-center gap-1 px-1.5 py-2 text-xs">
            <UnassignedSpendingNavIcon pendingCount={unassignedCount} iconClassName="h-5 w-5" />
            <span className="text-primary">Hold</span>
          </div>
        )}
        <NavLink
          to="/settings"
          className={({ isActive }) =>
            cn(
              'flex flex-col items-center justify-center gap-1 px-2 py-2 text-xs transition-colors',
              isActive
                ? 'text-primary'
                : 'text-muted-foreground hover:text-foreground'
            )
          }
        >
          <span className="h-5 w-5">{settingsAvatar}</span>
          <span>Settings</span>
        </NavLink>
        {user?.isAdmin && (
          <NavLink
            to="/admin"
            className={({ isActive }) =>
              cn(
                'flex flex-col items-center justify-center gap-1 px-2 py-2 text-xs transition-colors',
                isActive
                  ? 'text-primary'
                  : 'text-muted-foreground hover:text-foreground'
              )
            }
          >
            <span className="h-5 w-5">{navIcons.admin}</span>
            <span>Admin</span>
          </NavLink>
        )}
      </div>
    </nav>
  );
}
